import React, { Component } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { GQL } from "./resources/utils";

// screens
import Onboard from "./screens/Onboard";
import SignIn from "./screens/SignIn";
import How from "./screens/How";
import Terms from "./screens/Terms";
import What from "./screens/What";
import Enable from "./screens/Enable";
import Account from "./screens/Account";
import Tiers from "./screens/Tiers";
import Rewards from "./screens/Rewards";
import Reward from "./screens/Reward";
import Activity from "./screens/Activity";
import Confirm from "./screens/Confirm";

// Components
import Loading from "./components/Loading";
import Header from "./components/Header";
import Footer from "./components/Footer";

import styles from './App.module.scss';

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      config: null,
      loading: true,
      onboardsToShow: [],
      rewards: [],
      tiers: [],
      customer: null,
    };
  }

  getCustomer = async(id, name = null) => {
    let result = await GQL(`
      {
        getCustomer(id: "${id}") {
          id
          points
          lifetimePoints
          referral
          lifetimeSpend
          lifetimeOrders
          birthday
          accountEnabled
          tiers
          tags
        }
      }
    `);
    if(result && result.data && result.data.data) {
      this.setState({ customer: {
        ...result.data.data.getCustomer,
        id: id,
        name: name,
      } });
    }
  }

  onMessage = async(e) => {
    if(e && e.data && e.data.customer_id) {
      this.getCustomer(e.data.customer_id, e.data.customer_name)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMessage);
  }

  async componentDidMount() {
    window.addEventListener('message', this.onMessage);

    if(!this.state.config) {
      let configRaw = await axios.get('https://gg-loyalty-configs.s3-us-west-2.amazonaws.com/good-george-brewing-myshopify-com.js');
      let config = configRaw && configRaw.data ? configRaw.data : null;

      let appData = await GQL(`
        {
          getRewards {
            id
            points
            conditions
            addAction {
              id
              quantity
            }
            checkoutOnAddAction
            discount {
              __typename
              ... on UntrackedDiscount {
                instructions
              }
            }
          }
          getTiers {
            id
            conditions
          }
        }
      `);
      let rewards = appData && appData.data && appData.data.data && appData.data.data.getRewards ? appData.data.data.getRewards : [];
      let tiers = appData && appData.data && appData.data.data && appData.data.data.getRewards ? appData.data.data.getTiers : [];

      if(config) {
        let viewed_onboards = null;
        try {
          viewed_onboards = localStorage.getItem('ggl@onboards');
        } catch(e) {
          console.log(e);
        }
        let voArr = viewed_onboards ? viewed_onboards.split(',') : [];
        let onboardsToShow = config.onboards.filter(x => !voArr.includes(x.id));
        this.setState({ config, loading: false, onboardsToShow, rewards, tiers });
      }
    }

    // manually set the customer for testing, comment out when you don't need it.
    // this.getCustomer("3821913768073", "Mitchell");
  }

  renderRoot = () => {
    const { config, onboardsToShow, customer, rewards, tiers } = this.state;
    if(onboardsToShow.length) {
      return <Onboard onboards={config.onboards} onFinish={async() => {
        // const viewed_onboards = localStorage.getItem('ggl@onboards');
        try {
          let newOnboardsToFilter = [ ...onboardsToShow.map(x => x.id) ];
          localStorage.setItem('ggl@onboards', newOnboardsToFilter.join(','));
        } catch(e) {
          console.log(e);
        }
        this.setState({ onboardsToShow: [] });
      }} />
    } else if(customer) {
      if(customer.accountEnabled) {
        return <Account customer={customer} config={config} rewards={rewards} tiers={tiers} />
      } else {
        return <Enable id={customer.id} config={config} callback={(customerResponse) => {
          let newCustomer = {
            ...customer,
            ...customerResponse
          }
          this.setState({ customer: newCustomer })
        }} />
      }
    } else {
      return <SignIn config={config} />
    }
  }

  render() {
    const { loading, customer, onboardsToShow } = this.state;

    if(loading) {
      return (
        <div className={styles.popup}>
          <Loading />
        </div>
      )
    }

    /*

    / = account/signin/onboards managed by state/props.
    /activity
    /rewards
      /rewardId
        /buy?
    /tiers

    */


    return (
      <div className={styles.popup}>
        <Router>
          <Header />
          <AnimatedSwitch state={this.state} renderRoot={this.renderRoot.bind(this)} />
          {
            customer && customer.accountEnabled && !onboardsToShow.length &&
            <Footer />
          }
        </Router>
      </div>
    );
  }
}

const AnimatedSwitch = ({ state, renderRoot }) => {
  const { config, rewards, tiers, customer } = state;
  const location = useLocation();
  return (
    <AnimatePresence initial={false}>
      <Switch location={location} key={location.pathname}>
        <Route path="/how">
          <How config={config} customer={customer} />
        </Route>
        <Route path="/what">
          <What config={config} customer={customer} rewards={rewards} tiers={tiers} />
        </Route>
        <Route path="/terms">
          <Terms config={config} customer={customer} />
        </Route>
        <Route path="/rewards">
          <RewardsSwitch customer={customer} config={config} rewards={rewards} />
        </Route>
        <Route path="/tiers">
          <Tiers customer={customer} config={config} rewards={rewards} tiers={tiers} />
        </Route>
        <Route path="/activity">
          <Activity customer={customer} config={config} rewards={rewards} tiers={tiers} />
        </Route>
        <Route path="/">
          { renderRoot }
        </Route>
      </Switch>
    </AnimatePresence>
  )
}

function RewardsSwitch({ config, customer, rewards }) {
  let match = useRouteMatch();

  return (
    <Switch>
      <Route path={`${match.path}/confirm/:rewardID`}>
        <Confirm customer={customer} config={config} rewards={rewards} />
      </Route>
      <Route path={`${match.path}/:rewardID`}>
        <Reward customer={customer} config={config} rewards={rewards} />
      </Route>
      <Route path={match.path}>
        <Rewards customer={customer} config={config} rewards={rewards} />
      </Route>
    </Switch>
  );
}

export default App;
